/**
 * A single Served-wall case card. Shows a field photo when available (with before/after
 * hover-compare), else a dignified brand illustration. "Verified" badge, category,
 * anonymised label, village, date, amount. Links to the public proof-chain detail.
 */
import { Link } from 'react-router-dom';
import { BadgeCheck, MapPin } from 'lucide-react';
import { CategoryArt } from '../illustrations/CategoryArt.jsx';
import { CATEGORY_LABEL, formatINR, formatDate } from '../../lib/format.js';
import { categoryIcon } from '../../data/categories.js';
import styles from './ServedCard.module.css';

export function ServedCard({ item }) {
  const hasPhotos = item.beforePhoto && item.afterPhoto;
  const CatIcon = categoryIcon(item.category);
  return (
    <Link to={`/served/${item.id}`} className={styles.card}>
      <div className={styles.media}>
        {item.afterPhoto ? (
          <>
            <img className={styles.after} src={item.afterPhoto} alt="After help was delivered" loading="lazy" />
            {hasPhotos && (
              <img className={styles.before} src={item.beforePhoto} alt="Before help" loading="lazy" />
            )}
            {hasPhotos && <span className={styles.compareHint}>Hover to see before</span>}
          </>
        ) : (
          <div className={styles.art}>
            <CategoryArt category={item.category} />
          </div>
        )}
        <span className={styles.badge}>
          <BadgeCheck size={14} /> Verified
        </span>
        <span className={styles.cat}>
          <CatIcon size={13} aria-hidden="true" /> {CATEGORY_LABEL[item.category]}
        </span>
      </div>
      <div className={styles.body}>
        <div className={styles.topRow}>
          <strong className={styles.label}>{item.label}</strong>
          <span className={`${styles.amount} mono`}>{formatINR(item.amount, { compact: true })}</span>
        </div>
        <p className={styles.summary}>{item.summary}</p>
        <div className={styles.foot}>
          <span className={styles.place}>
            <MapPin size={13} /> {item.village || item.block || 'Constituency'}
          </span>
          <span className={styles.date}>{formatDate(item.dateServed)}</span>
        </div>
      </div>
    </Link>
  );
}

export function ServedCardSkeleton() {
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={`${styles.media} ${styles.skel}`} />
      <div className={styles.body}>
        <div className={`${styles.skelLine} ${styles.skel}`} style={{ width: '60%' }} />
        <div className={`${styles.skelLine} ${styles.skel}`} style={{ width: '90%' }} />
        <div className={`${styles.skelLine} ${styles.skel}`} style={{ width: '40%' }} />
      </div>
    </div>
  );
}

export default ServedCard;
